import React from 'react';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';

import { Box, Button, Heading } from '@rover/kibble/core';
import events from '@rover/react-lib/src/pages/search/SearchPage/analytics';
import { getWizardTitle } from '@rover/react-lib/src/pages/search/SearchPage/utilities';
import { WizardPageType } from '@rover/react-lib/src/pages/search/SearchPage/WizardPageType';
import type { SearchFiltersOptions } from '@rover/types/src/apiResponses/SearchOptionsResponse';
import type { DataLayerEvent } from '@rover/types/src/DataLayerEvent';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import type { ServiceTypeOption } from '@rover/types/src/ServiceType';

import { PageOneWizardCollection, PageTwoWizardCollection } from './Wizard';

export type Props = {
  fireAnalyticsEvent: (
    arg0: DataLayerEvent | ((filters: Partial<SearchFilters>) => DataLayerEvent)
  ) => void;
  fireSearch: (arg0?: { searchInitiatedByMap: boolean }) => void;
  isCatOnly: boolean;
  onSubmit: () => void;
  searchFilters: SearchFilters;
  serviceTypeFilterOptions: SearchFiltersOptions;
  serviceTypeOptions: ServiceTypeOption[];
  updateFilters: (searchFilters: Partial<SearchFilters>) => void;
  fireSearchPageSearchCTAEvent: () => void;
};

const WizardFiltersCollectionBase = ({
  fireAnalyticsEvent,
  fireSearch,
  isCatOnly,
  onSubmit,
  searchFilters,
  serviceTypeFilterOptions,
  serviceTypeOptions,
  updateFilters,
  fireSearchPageSearchCTAEvent,
}: Props): JSX.Element => {
  const { i18n } = useLingui();
  const handleChange = (filters, shouldFireSearch = true): void => {
    updateFilters(filters);

    if (shouldFireSearch) {
      fireSearch();
    }
  };

  const handleCloseWizard = (): void => {
    fireAnalyticsEvent(events.SearchOwnerPreferencesCloseEvent);
    fireSearchPageSearchCTAEvent();
    onSubmit();
  };

  return (
    <Box p="4x" pt="0x" borderBottom="1px solid" borderColor="border.primary">
      <Heading size="300" webProps={{ as: 'h4' }} pt="4x">
        {i18n._(getWizardTitle(isCatOnly))}
      </Heading>
      <PageOneWizardCollection
        isCatOnly={isCatOnly}
        onChange={handleChange}
        pageType={WizardPageType.INLINE}
        searchFilters={searchFilters}
        serviceTypeFilterOptions={serviceTypeFilterOptions}
        serviceTypeOptions={serviceTypeOptions}
      />
      <PageTwoWizardCollection
        isCatOnly={isCatOnly}
        onChange={handleChange}
        pageType={WizardPageType.INLINE}
        searchFilters={searchFilters}
        serviceTypeFilterOptions={serviceTypeFilterOptions}
      />
      <Button
        data-qa-id="wizard-search-collection-save-button"
        variant="primary"
        fullWidth
        onClick={handleCloseWizard}
      >
        <Trans>Save search information</Trans>
      </Button>
    </Box>
  );
};

const customComparison = (prevProps: Props, nextProps: Props): boolean =>
  JSON.stringify(prevProps.searchFilters) === JSON.stringify(nextProps.searchFilters) &&
  JSON.stringify(prevProps.serviceTypeFilterOptions) ===
    JSON.stringify(nextProps.serviceTypeFilterOptions) &&
  JSON.stringify(prevProps.serviceTypeOptions) === JSON.stringify(nextProps.serviceTypeOptions);

const WizardFiltersCollection = React.memo(WizardFiltersCollectionBase, customComparison);

export { WizardFiltersCollection };
export default WizardFiltersCollection;
